import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {SwUpdate} from "@angular/service-worker";
import {isPlatformBrowser} from "@angular/common";
import {fromEvent, merge, map, Observable, EMPTY, Subject} from "rxjs";
import {DeviceService} from "./device.service";
import {environment} from "@app/environment";
import {MessageService} from "primeng/api";

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private connectionMonitor: Observable<boolean>;
  protected readonly appVersion = environment.VERSION;

  constructor(@Inject(PLATFORM_ID) platform: any,
              private swUpdate: SwUpdate,
              private messageService: MessageService,
              private deviceService: DeviceService) {
    // Connection Monitor
    if (isPlatformBrowser(platform)) {
      const offline$ = fromEvent(window, 'offline').pipe(map(() => false ));
      const online$ = fromEvent(window, 'online').pipe(map(() => true ));
      this.connectionMonitor = merge(
        offline$, online$
      );
    } else {
      this.connectionMonitor = EMPTY;
    }

    // Software Update
    swUpdate.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          console.log(`Downloading new app version: ${evt.version.hash}`);
          this.messageService.add({
            severity: 'info',
            summary: 'Update available',
            detail: 'Downloading new app version...',
            sticky: true
          });
          break;
        case 'VERSION_READY':
          console.log(`Current app version: ${evt.currentVersion.hash}`);
          console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
          this.cleanupLegacySettings();
          console.debug('App has been updated, reloading...');

          this.messageService.add({
            severity: 'info',
            summary: 'Update complete',
            detail: 'Restarting...',
            sticky: true
          });
          setTimeout(async () => {
            const activate = await this.swUpdate.activateUpdate();
            console.debug('App update: '+activate);
            window.location.reload();
          },2000)
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to install app version '${evt.version.hash}': ${evt.error}`,
            sticky: true
          });
          break;
      }
    });

    // Show the iOS install prompt modal
    const isInStandaloneMode = () => ('standalone' in (window as any).navigator) && ((window as any).navigator.standalone);
    if(deviceService.device.IOS && !isInStandaloneMode() && !environment.test) {
      // FIXME: Display a better dialog with iOS Share Icon
      this.messageService.add({
        severity: 'info',
        summary: 'Success',
        detail: 'Installa questa App sul tuo device...',
        sticky: false
      });
    }
  }

  networkMonitor(): Subject<boolean> {
    const subject = new Subject<boolean>();
    this.connectionMonitor.subscribe(subject);
    return subject;
  }

  /**
   * Compares two version strings.
   * Returns 1 if v1 > v2, -1 if v1 < v2, 0 if equal.
   */
  private compareVersions(v1: string, v2: string): number {
    const parse = (v: string) => {
      const parts = v.split(/[.\-b]/);
      return {
        nums: parts.map(p => parseInt(p, 10)).filter(n => !isNaN(n)),
        isBeta: v.includes('b') || v.includes('-') // Detects if it's a pre-release
      };
    };

    const a = parse(v1);
    const b = parse(v2);
    const maxLength = Math.max(a.nums.length, b.nums.length);

    for (let i = 0; i < maxLength; i++) {
      const num1 = a.nums[i] || 0;
      const num2 = b.nums[i] || 0;
      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    // If the numbers are identical, but one is a beta and the other isn't:
    // The one WITHOUT the beta flag is newer.
    if (a.isBeta && !b.isBeta) return -1;
    if (!a.isBeta && b.isBeta) return 1;

    return 0;
  }

  cleanupLegacySettings() {
    const storedVersion = localStorage.getItem('app_version') || '0.0.0';
    console.log(`Currently installed version: ${storedVersion}`);
    console.log(`New installed version: ${this.appVersion}`);

    // Cleanup only if the old version is lower than the treeshold
    if (this.compareVersions(storedVersion, environment.PWA_SETTINGS_CLEANUP_VERSION) === -1) {
      console.log(`Cleaning up local storage settings....`);
      Object.keys(localStorage)
        .filter(key => key.startsWith('settings.component.'))
        .forEach(key => localStorage.removeItem(key));

      localStorage.setItem('app_version', this.appVersion);
    }
  }

}
